import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, FileClock, Plus, Send, ShieldCheck } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  get_cms_document_by_id,
  publish_cms_documents,
  update_cms_documents,
} from "../../reduxData/cms/cmsAction";
import {
  Badge,
  ConfirmModal,
  DocumentStatusToggle,
  LanguageTabs,
  PageHeader,
} from "./CmsDocumentComponents.js";
import {
  apiError,
  formatVersion,
  getDocumentContent,
  getDocumentTitle,
  humanize,
  sanitizeHtml,
} from "./cmsDocumentUtils";
import { formatDateTime } from "../../hooks/formatDateTime";
import "../../css/cmsDocuments.css";

export default function DocumentDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const document = useSelector((state) => state.cms.cmsDocument);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [language, setLanguage] = useState("EN");
  const load = useCallback(
    () =>
      get_cms_document_by_id(id, dispatch).catch((e) =>
        setError(apiError(e, t("cmsDocuments.errors.loadDocument"))),
      ),
    [dispatch, id],
  );
  useEffect(() => {
    load();
  }, [load]);
  const mutate = async (fn, message) => {
    setBusy(true);
    try {
      await fn();
      toast.success(message);
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(apiError(e, t("cmsDocuments.errors.updateDocumentShort")));
    } finally {
      setBusy(false);
    }
  };
  if (error)
    return (
      <main className="cmsd-page">
        <div className="cmsd-state">
          <h2>{t("cmsDocuments.errors.loadDocumentTitle")}</h2>
          <p>{error}</p>
          <button
            className="btn cmsd-primary"
            onClick={() => navigate("/cms/documents")}
          >
            {t("cmsDocuments.actions.backToDocuments")}
          </button>
        </div>
      </main>
    );
  if (!document)
    return (
      <main className="cmsd-page">
        <div className="cmsd-state">
          <div className="spinner-border text-warning" />
          <p>{t("cmsDocuments.shared.loadingDocument")}</p>
        </div>
      </main>
    );
  return (
    <main className="cmsd-page">
      <PageHeader
        title={getDocumentTitle(document, language)}
        description={t("cmsDocuments.detail.description", { type: humanize(document.type), visibility: humanize(document.visibility) })}
      >
        <Link className="btn btn-light" to="/cms/documents">
          <ArrowLeft size={17} /> {t("cmsDocuments.list.title")}
        </Link>
        <DocumentStatusToggle
          item={document}
          busy={busy}
          onToggle={(item) =>
            mutate(
              () =>
                update_cms_documents(
                  id,
                  {
                    status:
                      item.status === "PUBLISHED" ? "ARCHIVED" : "PUBLISHED",
                    title: item.title,
                    content: item.content,
                    visibility: item.visibility,
                  },
                  dispatch,
                ),
              item.status === "PUBLISHED"
                ? t("cmsDocuments.messages.deactivated")
                : t("cmsDocuments.messages.activated"),
            )
          }
        />
        {document.status !== "ARCHIVED" && (
          <Link
            className="btn btn-light"
            to={`/cms/documents/${id}/new-version`}
          >
            <Plus size={17} /> {t("cmsDocuments.actions.newVersion")}
          </Link>
        )}
        <Link className="btn btn-light" to={`/cms/documents/${id}/versions`}>
          <FileClock size={17} /> {t("cmsDocuments.actions.versions")}
        </Link>
        {document.status === "DRAFT" && (
          <button
            className="btn cmsd-primary"
            onClick={() => setConfirm("publish")}
          >
            <Send size={17} /> {t("cmsDocuments.actions.publish")}
          </button>
        )}
      </PageHeader>
      <section className="cmsd-detail-hero">
        <div>
          <span className="cmsd-detail-icon">
            <ShieldCheck size={22} />
          </span>
          <div>
            <small>{t("cmsDocuments.history.currentDocument")}</small>
            <h2>{getDocumentTitle(document, language)}</h2>
            <div className="d-flex gap-2 flex-wrap">
              <Badge value={document.type} kind="visibility" />
              <Badge value={document.visibility} kind="visibility" />
              <Badge value={document.status} />
              <span className="cmsd-version">
                {formatVersion(document.version)}
              </span>
            </div>
          </div>
        </div>
        <LanguageTabs value={language} onChange={setLanguage} />
      </section>
      <section className="cmsd-detail-card cmsd-content-paper">
        <div className="cmsd-content-heading">
          <div>
            <span>{t("cmsDocuments.detail.documentContent")}</span>
            <h2>{t("cmsDocuments.detail.languageVersion", { language: t(language === "EN" ? "cmsContent.languages.english" : "cmsContent.languages.dutch") })}</h2>
          </div>
          <span className="cmsd-html-label">&lt;/&gt; HTML</span>
        </div>
        <article
          className="cmsd-document-preview"
          lang={language.toLowerCase()}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(getDocumentContent(document, language)),
          }}
        />
      </section>
      <section className="cmsd-detail-card">
        <div className="cmsd-detail-title">
          <h2>{t("cmsDocuments.detail.documentInformation")}</h2>
        </div>
        <dl className="cmsd-metadata">
          <Meta label={t("cmsDocuments.form.documentType")} value={humanize(document.type)} />
          <Meta label={t("cmsDocuments.table.visibility")} value={humanize(document.visibility)} />
          <Meta label={t("cmsDocuments.table.status")} value={humanize(document.status)} />
          <Meta
            label={t("cmsDocuments.detail.currentVersion")}
            value={formatVersion(document.version)}
          />
          <Meta
            label={t("cmsDocuments.detail.englishTitle")}
            value={getDocumentTitle(document, "EN")}
          />
          <Meta label={t("cmsDocuments.detail.dutchTitle")} value={getDocumentTitle(document, "NL")} />
        </dl>
      </section>
      <div className="cmsd-two-column">
        <section className="cmsd-detail-card">
          <h2>{t("cmsDocuments.detail.publishingInformation")}</h2>
          <dl className="cmsd-metadata cmsd-metadata--single">
            <Meta label={t("cmsDocuments.table.createdAt")} value={formatDateTime(document.createdAt, i18n, false)} />
            <Meta
              label={t("cmsDocuments.detail.createdBy")}
              value={displayPerson(document.user_name)}
            />            
            <Meta
              label={t("cmsDocuments.table.publishedAt")}
              value={formatDateTime(document.publishedAt, i18n, false)}
            />           
          </dl>
        </section>
        <section className="cmsd-detail-card">
          <h2>{t("cmsDocuments.detail.versionInformation")}</h2>
          <dl className="cmsd-metadata cmsd-metadata--single">
            <Meta label={t("cmsDocuments.table.version")} value={formatVersion(document.version)} />
            <Meta label={t("cmsDocuments.detail.versionStatus")} value={humanize(document.status)} />
          </dl>
        </section>
      </div>
      <ConfirmModal
        action={confirm}
        item={document}
        busy={busy}
        onClose={() => setConfirm(null)}
        onConfirm={() =>
          mutate(
            () => publish_cms_documents(id, dispatch),
            t("cmsDocuments.messages.published"),
          )
        }
      />
    </main>
  );
}
const Meta = ({ label, value }) => (
  <div>
    <dt>{label}</dt>
    <dd>{value || "—"}</dd>
  </div>
);
const displayPerson = (person) =>
  person && typeof person === "object"
    ? (person.name ?? person.email ?? person.id ?? "—")
    : person || "—";
